import * as z from 'zod';
import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_SCHEDULED_VALUES,
  MODULE,
} from '../common';
import type { Prisma, ValueCategory } from '@prisma/client';
import { ValueCategoryEnum } from '@jobcost-monorepo/database/prisma/enums';

type ScheduledValue = Prisma.$ScheduledValuePayload['scalars'];

export type IScheduledValue = ScheduledValue &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'>;

export interface IListScheduledValuesFetchParams extends IListFetchParams {
  valueCategory?: ValueCategory;
  startDate?: string;
  endDate?: string;
  isSale?: boolean;
}

class ScheduledValueRepository extends BaseRepository<
  IScheduledValue,
  IListScheduledValuesFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['description'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'description',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'valueCategory',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'isSale',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'boolean',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'startDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'endDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'valueCategory',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
        enumValues: ValueCategoryEnum,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'startDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        position: { x: 1, y: 1 },
        linkedField: 'endDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        position: { x: 0, y: 2 },
        linkedField: 'isSale',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'description',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'valueCategory',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
        enumValues: ValueCategoryEnum,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'isSale',
        type: 'boolean',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'startDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'endDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'daysBetween',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 3 },
        linkedField: 'fixedDay',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'netValue',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 4 },
        linkedField: 'vatValue',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 4 },
        linkedField: 'grossValue',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 5 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_SCHEDULED_VALUES,
        translationModuleName: MN_SCHEDULED_VALUES,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      description: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      valueCategory: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      isSale: z.boolean({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      startDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      endDate: z.string().nullable().optional(),
      daysBetween: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .nullable()
        .optional(),
      fixedDay: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .nullable()
        .optional(),
      netValue: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      vatValue: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      grossValue: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default ScheduledValueRepository;
